import { useEffect, useState } from "react";
import api, { inr, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, AlertTriangle, ArrowDown, ArrowUp } from "lucide-react";
import { toast } from "sonner";

export default function Inventory() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", unit: "kg", quantity: 0, min_quantity: 0, cost: 0 });

  const [adjOpen, setAdjOpen] = useState(false);
  const [adjItem, setAdjItem] = useState(null);
  const [adjDelta, setAdjDelta] = useState(0);
  const [adjReason, setAdjReason] = useState("");

  const load = () => api.get("/inventory").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const startNew = () => { setEditing(null); setForm({ name: "", unit: "kg", quantity: 0, min_quantity: 0, cost: 0 }); setOpen(true); };
  const startEdit = (i) => { setEditing(i); setForm({ name: i.name, unit: i.unit, quantity: i.quantity, min_quantity: i.min_quantity, cost: i.cost }); setOpen(true); };
  const save = async () => {
    try {
      const payload = { ...form, quantity: Number(form.quantity), min_quantity: Number(form.min_quantity), cost: Number(form.cost) };
      if (editing) await api.put(`/inventory/${editing.id}`, payload);
      else await api.post("/inventory", payload);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const del = async (i) => {
    if (!window.confirm(`Delete ${i.name}?`)) return;
    try { await api.delete(`/inventory/${i.id}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const startAdj = (i) => { setAdjItem(i); setAdjDelta(0); setAdjReason(""); setAdjOpen(true); };
  const submitAdj = async () => {
    try {
      await api.post(`/inventory/${adjItem.id}/adjust`, { delta: Number(adjDelta), reason: adjReason });
      toast.success("Stock adjusted"); setAdjOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-6 sm:p-8" data-testid="inventory-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading font-extrabold text-3xl text-[#2D2C2A]">Inventory</h1>
          <p className="text-sm text-[#7A7873] mt-1">Track stock, receive deliveries, get low-stock alerts.</p>
        </div>
        <Button onClick={startNew} className="bg-[#D95D39] hover:bg-[#C24F2C]" data-testid="add-inv-btn">
          <Plus size={16} className="mr-1.5" /> Add Item
        </Button>
      </div>

      <div className="bg-white border border-[#EBE8E2] rounded-lg overflow-x-auto">
        <table className="w-full text-sm min-w-[700px]">
          <thead className="bg-[#F4F2EE] text-[#7A7873] text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-2.5">Item</th>
              <th className="text-left px-4 py-2.5">Unit</th>
              <th className="text-right px-4 py-2.5">Quantity</th>
              <th className="text-right px-4 py-2.5">Min</th>
              <th className="text-right px-4 py-2.5">Cost</th>
              <th className="text-right px-4 py-2.5">Status</th>
              <th className="px-4 py-2.5"></th>
            </tr>
          </thead>
          <tbody>
            {list.map((i) => {
              const low = i.quantity <= i.min_quantity;
              return (
                <tr key={i.id} className="border-t border-[#EBE8E2]" data-testid={`inv-row-${i.id}`}>
                  <td className="px-4 py-2.5 font-medium">{i.name}</td>
                  <td className="px-4 py-2.5 text-[#7A7873]">{i.unit}</td>
                  <td className="px-4 py-2.5 text-right font-mono-num font-semibold">{i.quantity}</td>
                  <td className="px-4 py-2.5 text-right font-mono-num text-[#7A7873]">{i.min_quantity}</td>
                  <td className="px-4 py-2.5 text-right font-mono-num">{inr(i.cost)}</td>
                  <td className="px-4 py-2.5 text-right">
                    {low ? (
                      <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-[#FBE2DE] text-[#E76F51]">
                        <AlertTriangle size={11} /> Low
                      </span>
                    ) : (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-[#E5EBE3] text-[#6A7F66]">OK</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <Button size="sm" variant="outline" className="h-7 mr-1" onClick={() => startAdj(i)} data-testid={`adjust-${i.id}`}>Adjust</Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => startEdit(i)}>
                      <Pencil size={13} />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-[#E76F51]" onClick={() => del(i)}>
                      <Trash2 size={13} />
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>{editing ? "Edit Item" : "Add Item"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="inv-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Unit</Label><Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} /></div>
              <div><Label>Cost per unit</Label><Input type="number" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Quantity</Label><Input type="number" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} /></div>
              <div><Label>Min Quantity</Label><Input type="number" value={form.min_quantity} onChange={(e) => setForm({ ...form, min_quantity: e.target.value })} /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="bg-[#D95D39] hover:bg-[#C24F2C]" onClick={save} data-testid="save-inv">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={adjOpen} onOpenChange={setAdjOpen}>
        <DialogContent className="bg-white max-w-sm">
          <DialogHeader><DialogTitle>Adjust Stock · {adjItem?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1 text-[#6A7F66]" onClick={() => setAdjDelta(Math.abs(Number(adjDelta || 0)))}>
                <ArrowUp size={14} className="mr-1" /> Receive (+)
              </Button>
              <Button variant="outline" className="flex-1 text-[#E76F51]" onClick={() => setAdjDelta(-Math.abs(Number(adjDelta || 0)))}>
                <ArrowDown size={14} className="mr-1" /> Issue (−)
              </Button>
            </div>
            <div><Label>Delta (use + or −)</Label><Input type="number" value={adjDelta} onChange={(e) => setAdjDelta(e.target.value)} data-testid="adj-delta" /></div>
            <div><Label>Reason</Label><Input value={adjReason} onChange={(e) => setAdjReason(e.target.value)} placeholder="e.g. Purchase, Wastage" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdjOpen(false)}>Cancel</Button>
            <Button className="bg-[#D95D39] hover:bg-[#C24F2C]" onClick={submitAdj} data-testid="save-adj">Apply</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
